/** Painéis com largura persistida e arraste sem renderizar os filhos a cada movimento. */
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { alpha, makeStyles } from "@material-ui/core/styles";

const clamp = (width, limits) => Math.min(limits.max, Math.max(limits.min, width));

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flex: 1,
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
    position: "relative",
    '&[data-resizing="true"]': {
      userSelect: "none",
      "& *": { userSelect: "none !important", cursor: "col-resize !important" },
      "& $separator": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
      "& $separator::before": { backgroundColor: theme.palette.primary.main, width: 2 },
    },
  },
  primary: {
    display: "flex",
    flexDirection: "column",
    flex: "0 0 33.333333%",
    width: "33.333333%",
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    overflow: "hidden",
    position: "relative",
  },
  secondary: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    overflow: "hidden",
    position: "relative",
  },
  separator: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: "calc(33.333333% - 4px)",
    width: 8,
    zIndex: 10,
    cursor: "col-resize",
    touchAction: "none",
    userSelect: "none",
    outline: "none",
    transition: theme.transitions.create("background-color", { duration: theme.transitions.duration.shortest }),
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      left: "50%",
      width: 1,
      transform: "translateX(-50%)",
      backgroundColor: theme.palette.divider,
      transition: theme.transitions.create("background-color", { duration: theme.transitions.duration.shortest }),
    },
    "&:hover, &:focus-visible": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
    "&:hover::before, &:focus-visible::before": { backgroundColor: theme.palette.primary.main },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&::before": { transition: "none" },
    },
  },
}));

const ResizableSplitView = ({
  primary,
  secondary,
  primaryId,
  secondaryClassName = "",
  storageKey,
  separatorLabel,
  separatorHint,
  minPrimaryWidth = 320,
  maxPrimaryWidth = 650,
  minSecondaryWidth = 480,
  defaultRatio = 1 / 3,
}) => {
  const classes = useStyles();
  const rootRef = useRef(null);
  const primaryRef = useRef(null);
  const separatorRef = useRef(null);
  const widthRef = useRef(0);
  const preferredWidthRef = useRef(null);
  const dragRef = useRef(null);
  const frameRef = useRef(null);
  const measureRef = useRef(null);
  const limitsRef = useRef({ min: 0, max: 0, enabled: false });
  const [limits, setLimits] = useState(limitsRef.current);

  const applyWidth = useCallback((width) => {
    const next = clamp(width, limitsRef.current);
    if (next === widthRef.current) return next;
    widthRef.current = next;
    // Estilos locais evitam invalidar uma variável herdada por toda a conversa.
    if (primaryRef.current) {
      primaryRef.current.style.flexBasis = `${next}px`;
      primaryRef.current.style.width = `${next}px`;
    }
    if (separatorRef.current) {
      separatorRef.current.style.left = `${next - 4}px`;
      separatorRef.current.setAttribute("aria-valuenow", String(Math.round(next)));
    }
    return next;
  }, []);

  const persist = useCallback((width) => {
    preferredWidthRef.current = width;
    try {
      if (storageKey) localStorage.setItem(storageKey, String(width));
    } catch {
      // A preferência continua válida nesta tela se o armazenamento estiver bloqueado.
    }
  }, [storageKey]);

  const finishDrag = useCallback((commit) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    window.removeEventListener("pointermove", drag.move);
    window.removeEventListener("pointerup", drag.end);
    window.removeEventListener("pointercancel", drag.cancel);
    window.removeEventListener("blur", drag.blur);
    if (rootRef.current) rootRef.current.removeAttribute("data-resizing");
    drag.bodyStyles.forEach(([property, value, priority]) => {
      if (value) document.body.style.setProperty(property, value, priority);
      else document.body.style.removeProperty(property);
    });
    try {
      if (drag.handle.releasePointerCapture) drag.handle.releasePointerCapture(drag.pointerId);
    } catch { /* O navegador pode já ter liberado a captura. */ }
    const width = applyWidth(commit ? drag.nextWidth : drag.startWidth);
    if (commit) persist(width);
  }, [applyWidth, persist]);

  useLayoutEffect(() => {
    preferredWidthRef.current = null;
    try {
      const stored = storageKey ? localStorage.getItem(storageKey) : null;
      const parsed = Number(stored);
      if (stored && Number.isFinite(parsed) && parsed > 0) {
        preferredWidthRef.current = Math.min(maxPrimaryWidth, Math.max(minPrimaryWidth, parsed));
      }
    } catch { /* Sem armazenamento, mantém a divisão padrão. */ }

    const measure = () => {
      const total = rootRef.current?.clientWidth;
      if (!total) return;
      const enabled = total >= minPrimaryWidth + minSecondaryWidth;
      // Quando os mínimos não cabem, conserva a divisão proporcional existente.
      const proportional = total * defaultRatio;
      const nextLimits = enabled
        ? { min: minPrimaryWidth, max: Math.min(maxPrimaryWidth, total - minSecondaryWidth), enabled }
        : { min: proportional, max: proportional, enabled };
      limitsRef.current = nextLimits;
      if (dragRef.current && !enabled) finishDrag(false);
      if (dragRef.current) {
        dragRef.current.nextWidth = clamp(dragRef.current.nextWidth, nextLimits);
        applyWidth(dragRef.current.nextWidth);
      } else {
        applyWidth(preferredWidthRef.current ?? proportional);
      }
      setLimits((previous) => previous.min === nextLimits.min && previous.max === nextLimits.max && previous.enabled === enabled ? previous : nextLimits);
    };
    measureRef.current = measure;
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(rootRef.current);
    else window.addEventListener("resize", measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
      finishDrag(false);
      measureRef.current = null;
    };
  }, [storageKey, minPrimaryWidth, maxPrimaryWidth, minSecondaryWidth, defaultRatio, applyWidth, finishDrag]);

  const handlePointerDown = useCallback((event) => {
    if (!limitsRef.current.enabled || dragRef.current || event.button !== 0 || event.isPrimary === false) return;
    event.preventDefault();
    const handle = event.currentTarget;
    handle.focus();
    const properties = ["user-select", "-webkit-user-select", "cursor"];
    const drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: widthRef.current,
      nextWidth: widthRef.current,
      handle,
      bodyStyles: properties.map((property) => [property, document.body.style.getPropertyValue(property), document.body.style.getPropertyPriority(property)]),
    };
    drag.move = (moveEvent) => {
      if (moveEvent.pointerId !== drag.pointerId) return;
      drag.nextWidth = clamp(drag.startWidth + moveEvent.clientX - drag.startX, limitsRef.current);
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        if (dragRef.current === drag) applyWidth(drag.nextWidth);
      });
    };
    drag.end = (endEvent) => { if (endEvent.pointerId === drag.pointerId) finishDrag(true); };
    drag.cancel = (cancelEvent) => { if (cancelEvent.pointerId === drag.pointerId) finishDrag(false); };
    drag.blur = () => finishDrag(true);
    dragRef.current = drag;
    if (rootRef.current) rootRef.current.setAttribute("data-resizing", "true");
    document.body.style.setProperty("user-select", "none");
    document.body.style.setProperty("-webkit-user-select", "none");
    document.body.style.setProperty("cursor", "col-resize");
    window.addEventListener("pointermove", drag.move);
    window.addEventListener("pointerup", drag.end);
    window.addEventListener("pointercancel", drag.cancel);
    window.addEventListener("blur", drag.blur);
    try {
      if (handle.setPointerCapture) handle.setPointerCapture(event.pointerId);
    } catch { /* Eventos sintéticos não possuem captura. */ }
  }, [applyWidth, finishDrag]);

  const handleKeyDown = (event) => {
    if (!limitsRef.current.enabled || dragRef.current) return;
    const step = event.shiftKey ? 32 : 16;
    const widths = {
      ArrowLeft: widthRef.current - step,
      ArrowRight: widthRef.current + step,
      Home: limitsRef.current.min,
      End: limitsRef.current.max,
    };
    if (widths[event.key] === undefined) return;
    event.preventDefault();
    persist(applyWidth(widths[event.key]));
  };

  const resetWidth = () => {
    finishDrag(false);
    preferredWidthRef.current = null;
    try { if (storageKey) localStorage.removeItem(storageKey); } catch { /* Usa o padrão mesmo sem armazenamento. */ }
    if (measureRef.current) measureRef.current();
  };

  return (
    <div ref={rootRef} className={classes.root}>
      <div ref={primaryRef} id={primaryId} className={classes.primary}>{primary}</div>
      <div className={`${classes.secondary} ${secondaryClassName}`}>{secondary}</div>
      {/* O jsx-a11y antigo só reconhece separadores estáticos; este é um controle de largura. */}
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
      <div
        ref={separatorRef}
        className={classes.separator}
        style={{ display: limits.enabled ? undefined : "none" }}
        role="separator"
        aria-orientation="vertical"
        aria-label={separatorLabel}
        aria-controls={primaryId}
        aria-valuemin={Math.round(limits.min)}
        aria-valuemax={Math.round(limits.max)}
        aria-valuenow={Math.round(widthRef.current)}
        tabIndex={limits.enabled ? 0 : -1}
        title={separatorHint}
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
        onDoubleClick={resetWidth}
      />
    </div>
  );
};

export default ResizableSplitView;
